import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  useWindowDimensions,
  TextInput,
  Modal,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Svg, { Path, Circle, G } from 'react-native-svg';
import { COLORS, FONTS } from '../../constants/config';
import {
  PageContainer,
  Navbar,
  Breadcrumb,
  CommentSection,
  CommentData,
  getAvatarColorFromUserType,
  UserType,
  EvidenceTable,
} from '../../components';
import TargetAchievementCard from '../../components/TargetAchievementCard';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { servicesApi, kpisApi, goalsApi, serviceCommentsApi, ServiceComment as ApiServiceComment } from '../../services/apiService';
import { Service, KPIList } from '../../types';
import DeletionImpactModal from '../../components/DeletionImpactModal';
import DeletionApprovalBanner from '../../components/DeletionApprovalBanner';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';
import { useCachedData } from '../../hooks/useCachedData';
import { useResponsive, useRoleNavigation } from '../../hooks';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
import { PageHeader } from '@design/components/PageHeader';
type RouteProps = RouteProp<MainStackParamList, 'ServiceDetail'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

// ============================================================
// SVG Icon Components
// ============================================================

const ChevronLeftIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M15 18L9 12L15 6" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const EditIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M18.5 2.50001C18.8978 2.10219 19.4374 1.87869 20 1.87869C20.5626 1.87869 21.1022 2.10219 21.5 2.50001C21.8978 2.89784 22.1213 3.4374 22.1213 4.00001C22.1213 4.56262 21.8978 5.10219 21.5 5.50001L12 15L8 16L9 12L18.5 2.50001Z" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ExportIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M17 8L12 3L7 8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M12 3V15" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const PlusIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M12 5V19M5 12H19" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const ArrowRightIcon = ({ size = 16, color = tokens.colors.raw.white }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path
      d="M5 12H19M19 12L12 5M19 12L12 19"
      stroke={color}
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Svg>
);

// ============================================================
// Donut Chart Component
// ============================================================

interface DonutChartProps {
  percentage: number;
  size?: number;
  strokeWidth?: number;
}

/**
 * Get color based on achievement percentage
 * - >= 100%: Green (#22C55E) - ممتاز
 * - >= 80% and < 100%: Orange (#F97316) - جيد
 * - < 80%: Red (#EF4444) - يحتاج تحسين
 */
const getAchievementColor = (percentage: number): string => {
  if (percentage >= 100) return tokens.colors.raw.green["500"];  // Green - Excellent
  if (percentage >= 80) return tokens.colors.raw.orange["500"];   // Orange - Good
  return tokens.colors.raw.red["500"];                          // Red - Needs Improvement
};

const DonutChart = ({ percentage, size = 168, strokeWidth = 12 }: DonutChartProps) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  const center = size / 2;

  // Dynamic color based on achievement percentage
  const progressColor = getAchievementColor(percentage);

  // Calculate dot position at the end of progress arc
  const angle = ((percentage / 100) * 360 - 90) * (Math.PI / 180);
  const dotX = center + radius * Math.cos(angle);
  const dotY = center + radius * Math.sin(angle);

  return (
    <View style={{ width: size, height: size, alignItems: 'center', justifyContent: 'center' }}>
      <Svg width={size} height={size}>
        {/* Background circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={tokens.colors.raw.slate["200"]}
          strokeWidth={strokeWidth}
          fill="none"
        />
        {/* Progress circle */}
        <Circle
          cx={center}
          cy={center}
          r={radius}
          stroke={progressColor}
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        {/* Dot indicator at end of progress */}
        <Circle
          cx={dotX}
          cy={dotY}
          r={7.5}
          fill={progressColor}
          stroke="#FCFCFC"
          strokeWidth={2}
        />
      </Svg>
      <View style={{ position: 'absolute', alignItems: 'center' }}>
        <Text style={{ fontSize: tokens.typography["heading 4"]["font size"], fontFamily: FONTS.numbers, fontWeight: '500', color: tokens.colors.semantic.general.foreground }}>
          %{percentage}
        </Text>
      </View>
    </View>
  );
};

// ============================================================
// Pie Chart Component (Status Distribution)
// ============================================================

interface PieChartProps {
  data: { value: number; color: string; label: string }[];
  size?: number;
}

const PieChart = ({ data, size = 100 }: PieChartProps) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const center = size / 2;
  const outerRadius = size / 2 - 5;
  const innerRadius = outerRadius * 0.6; // Donut hole

  if (total === 0) {
    return (
      <Svg width={size} height={size}>
        <Circle cx={center} cy={center} r={outerRadius} fill={tokens.colors.raw.slate["200"]} />
        <Circle cx={center} cy={center} r={innerRadius} fill="white" />
      </Svg>
    );
  }

  let currentAngle = -90;

  const paths = data.map((item, index) => {
    const angle = (item.value / total) * 360;

    // Full circle: SVG arc can't draw 360°, use Circle instead
    if (angle >= 359.99) {
      return (
        <G key={index}>
          <Circle cx={center} cy={center} r={outerRadius} fill={item.color} />
        </G>
      );
    }

    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;

    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;

    const outerX1 = center + outerRadius * Math.cos(startRad);
    const outerY1 = center + outerRadius * Math.sin(startRad);
    const outerX2 = center + outerRadius * Math.cos(endRad);
    const outerY2 = center + outerRadius * Math.sin(endRad);
    const innerX1 = center + innerRadius * Math.cos(startRad);
    const innerY1 = center + innerRadius * Math.sin(startRad);
    const innerX2 = center + innerRadius * Math.cos(endRad);
    const innerY2 = center + innerRadius * Math.sin(endRad);

    const largeArc = angle > 180 ? 1 : 0;

    const d = [
      `M ${outerX1} ${outerY1}`,
      `A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${outerX2} ${outerY2}`,
      `L ${innerX2} ${innerY2}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${innerX1} ${innerY1}`,
      'Z',
    ].join(' ');

    return <Path key={index} d={d} fill={item.color} />;
  });

  return (
    <Svg width={size} height={size}>
      <G>{paths}</G>
      <Circle cx={center} cy={center} r={innerRadius} fill="white" />
    </Svg>
  );
};


// ============================================================
// Main Component
// ============================================================

export default function ServiceDetailScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { id } = route.params;
  const { showSuccess, showError } = useNotification();
  const { navigateToHome, canComment, isViewOnly } = useRoleNavigation();
  const { user } = useAuth();
  const { width } = useWindowDimensions();
  const { colors, isDark } = useTheme();
  const { t } = useLanguage();
  const { translate, translateOrg } = useAutoTranslate();
  const { selectedYear } = useYear();

  // Theme colors object
  const themeColors = {
    card: colors.card,
    cardBorder: colors.cardBorder,
    textPrimary: colors.textPrimary,
    textSecondary: colors.textSecondary,
    textMuted: colors.textMuted,
    iconDefault: colors.iconDefault,
    border: colors.border,
    backgroundAlt: colors.backgroundAlt,
  };

  // Unified with useResponsive — stack on tablet + mobile (< 1024px).
  const { isDesktop } = useResponsive();
  const isSmallScreen = !isDesktop;

  const { data: service, setData: setService, loading, setLoading } = useCachedData<Service>(`service-${id}`);
  const [kpis, setKpis] = useState<KPIList[]>([]);
  const [showDeletionImpact, setShowDeletionImpact] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const isOrgEmployee = user?.profile?.user_type === 'org_employee';
  const canApproveDeletion = ['org_executive', 'strategy_team', 'superuser'].includes(user?.profile?.user_type || '');
  const canManageEvidence = ['org_employee', 'coordinator', 'superuser'].includes(user?.profile?.user_type || '');
  const canViewAllEvidence = ['strategy_team', 'executive', 'performance_evaluator', 'evaluation_approver', 'superuser'].includes(user?.profile?.user_type || '');

  // Comments state
  const [apiComments, setApiComments] = useState<ApiServiceComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [showAddComment, setShowAddComment] = useState(false);
  const [editingComment, setEditingComment] = useState<CommentData | null>(null);


  // Convert API comment to display format (unified with GoalDetailScreen pattern)
  const convertApiComment = (apiComment: ApiServiceComment): CommentData => {
    // Get the actual author name from author_details
    const fullName = apiComment.author_details?.full_name || apiComment.author_details?.username || t('common.user');
    const nameParts = fullName.split(' ').filter(Boolean);
    const authorName = nameParts[0] || t('common.user'); // First name only

    // Generate initials from the actual name
    const initials = nameParts.length >= 2
      ? `${nameParts[0][0]} ${nameParts[1][0]}`
      : fullName.slice(0, 2);

    const isOwner = user?.id === apiComment.author;
    const userType = (apiComment.author_type || 'org_employee') as UserType;
    const avatarColor = getAvatarColorFromUserType(userType);

    return {
      id: apiComment.id,
      name: authorName,
      initials,
      date: new Date(apiComment.created_at).toLocaleDateString('ar-SA', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }),
      text: apiComment.content,
      avatarColor,
      showMenu: isOwner, // Only show menu for own comments
      isEdited: apiComment.is_edited,
      originalText: apiComment.original_content,
      userType,
    };
  };

  // Convert API comments to display format
  const comments = apiComments.map(convertApiComment);

  // Fetch service comments
  const fetchComments = useCallback(async () => {
    try {
      setCommentsLoading(true);
      const commentsData = await serviceCommentsApi.getForService(id);
      setApiComments(commentsData);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setCommentsLoading(false);
    }
  }, [id]);

  // Fetch service details
  const fetchService = useCallback(async () => {
    try {
      if (!service) setLoading(true);
      const data = await servicesApi.get(id);
      setService(data);

      // Use KPIs from service response (already filtered consistently with kpi_distribution)
      // This ensures the KPIs list matches the distribution data
      setKpis(data.kpis || []);

      // Fetch comments for this service
      fetchComments();
    } catch (error) {
      console.error('Error fetching service:', error);
      showError(t('services.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [id, showError, fetchComments]);

  // Auto-refresh when screen comes into focus
  useFocusEffect(
    useCallback(() => {
      fetchService();
    }, [fetchService])
  );

  // Refetch when year changes
  useEffect(() => {
    fetchService();
  }, [selectedYear]);

  // Comment handlers - unified with GoalDetailScreen pattern
  const handleAddComment = async (text: string) => {
    try {
      // Determine author_type based on user profile
      const authorType = user?.profile?.user_type === 'strategy_team' ? 'strategy_team' : 'org_employee';

      await serviceCommentsApi.create({
        service: id,
        content: text,
        comment_type: 'note',
        author_type: authorType,
      });
      showSuccess(t('comments.addSuccess'));
      setShowAddComment(false);
      fetchComments();
    } catch (error) {
      console.error('Error adding comment:', error);
      showError(t('comments.addFailed'));
    }
  };

  const handleEditComment = (comment: CommentData) => {
    setEditingComment(comment);
    setShowAddComment(true);
  };

  const handleUpdateComment = async (text: string) => {
    if (!editingComment) return;
    try {
      await serviceCommentsApi.update(editingComment.id, { content: text });
      showSuccess(t('comments.updateSuccess'));
      setEditingComment(null);
      setShowAddComment(false);
      fetchComments();
    } catch (error) {
      console.error('Error updating comment:', error);
      showError(t('comments.updateFailed'));
    }
  };

  const handleDelete = () => {
    setShowDeletionImpact(true);
  };

  const confirmDelete = async () => {
    try {
      setIsDeleting(true);
      const response = await servicesApi.delete(id);
      if ((response as any)?.status === 202) {
        showSuccess(t('deletion.requestSent'));
      } else {
        showSuccess(t('services.deleteSuccess'));
      }
      navigation.goBack();
    } catch (error: any) {
      if (error.response?.status === 202) {
        showSuccess(t('deletion.requestSent'));
        navigation.goBack();
      } else if (error.response?.status === 409) {
        showError(t('deletion.alreadyPending'));
      } else {
        showError(t('messages.deleteFailed'));
      }
    } finally {
      setIsDeleting(false);
      setShowDeletionImpact(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      const response = await serviceCommentsApi.delete(commentId);
      // Check if it's a pending delete request (org_employee)
      showSuccess(t('comments.deleteSuccess'));
      fetchComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
      showError(t('comments.deleteFailed'));
    }
  };

  const handleCommentSubmit = (text: string) => {
    if (editingComment) {
      handleUpdateComment(text);
    } else {
      handleAddComment(text);
    }
  };

  const handleCommentCancel = () => {
    setEditingComment(null);
    setShowAddComment(false);
  };

  // Use kpi_distribution from API response instead of local calculation
  const distribution = service?.kpi_distribution ?? { excellent: 0, good: 0, needs_improvement: 0 };
  const hasDistributionData = (distribution.excellent ?? 0) > 0 || (distribution.good ?? 0) > 0 || (distribution.needs_improvement ?? 0) > 0;

  const pieData = hasDistributionData ? [
    { value: distribution.excellent ?? 0, color: '#2ca02c', label: t('status.excellent') },
    { value: distribution.good ?? 0, color: '#ff7f0e', label: t('status.good') },
    { value: distribution.needs_improvement ?? 0, color: '#d62728', label: t('status.needsImprovement') },
  ].filter(item => item.value > 0) : [
    { value: 1, color: tokens.colors.raw.slate["200"], label: t('common.noData') },
  ];

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
        </View>
      </PageContainer>
    );
  }

  if (!service) {
    return (
      <PageContainer>
        <Navbar />
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{t('services.notFound')}</Text>
        </View>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ScrollView style={styles.scrollView} contentContainerStyle={[styles.content, isSmallScreen && styles.contentSmall]}>
        {/* Breadcrumb */}
        <Breadcrumb
          items={[
            { label: t('navigation.home') },
            { label: t('services.title'), route: 'Services' },
            { label: t('services.serviceDetails') },
          ]}
        />

        <PageHeader
          title={translate(service.name)}
          description={translate(service.description)}
          actions={
            <View style={styles.headerActions}>
              <Button
                variant="secondary"
                onPress={() => {}}
                trailingIcon={<ExportIcon size={16} color={themeColors.iconDefault} />}
              >
                {t('common.exportPDF')}
              </Button>
              {!isViewOnly && (
                <>
                  <Button variant="destructive" onPress={handleDelete}>
                    {t('common.delete')}
                  </Button>
                  <Button
                    variant="secondary"
                    onPress={() => navigation.navigate('AddService', { serviceId: id })}
                    trailingIcon={<EditIcon size={16} color={themeColors.iconDefault} />}
                  >
                    {t('common.edit')}
                  </Button>
                </>
              )}
            </View>
          }
        />

        {/* Pending Deletion Banner */}
        {(service as any).pending_deletion_info && (
          <DeletionApprovalBanner
            deletionInfo={(service as any).pending_deletion_info}
            canApprove={canApproveDeletion}
            canCancel={isOrgEmployee && (service as any).pending_deletion_info.requested_by_id === user?.id}
            onApproved={() => { showSuccess(t('deletion.approved')); navigation.goBack(); }}
            onRejected={() => { showSuccess(t('deletion.rejected')); fetchService(); }}
            onCancelled={() => { showSuccess(t('deletion.requestCancelled')); fetchService(); }}
            onViewImpact={() => setShowDeletionImpact(true)}
          />
        )}

        {/* Stats Cards Row */}
        <View style={[styles.statsRow, isSmallScreen && styles.statsRowSmall]}>
          {/* Basic Information Card - RIGHT */}
          <View style={[styles.statsCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.statsCardTitle, { color: themeColors.textPrimary }]}>{t('common.basicInfo')}</Text>
            <View style={styles.infoItemsContainer}>
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>{t('initiatives.implementationResponsibility')}</Text>
                <Text style={styles.infoValue}>
                  {(service.owning_units_details && service.owning_units_details.length > 0)
                    ? service.owning_units_details.map(u => translate(u.name_ar || u.name)).join('، ')
                    : t('common.notSpecified')}
                </Text>
              </View>
              <View style={styles.infoItem}>
                <Text style={styles.infoLabel}>{t('goals.strategicGoal')}</Text>
                <Text style={styles.infoValue}>
                  {translate(service.strategic_goal_title) || t('common.notSpecified')}
                </Text>
              </View>
            </View>
          </View>

          {/* Performance Indicators Card - MIDDLE (fixed width) */}
          <View style={[styles.statsCardFixed, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.statsCardTitle, { color: themeColors.textPrimary }]}>{t('kpis.performanceIndicators')}</Text>
            <View style={styles.chartContainer}>
              {service?.achievement_percentage != null ? (
                <DonutChart percentage={service.achievement_percentage} size={168} strokeWidth={12} />
              ) : (
                <View style={{ width: 168, height: 168, justifyContent: 'center', alignItems: 'center' }}>
                  <Text style={{ fontSize: 36, fontWeight: '700', color: themeColors.textMuted }}>--</Text>
                </View>
              )}
            </View>
          </View>

          {/* Status Distribution Card - LEFT */}
          <View style={[styles.statsCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.statsCardTitle, { color: themeColors.textPrimary }]}>{t('kpis.statusDistribution')}</Text>
            <View style={styles.distributionContent}>
              <PieChart data={pieData} size={140} />
              <View style={styles.legendContainer}>
                <View style={styles.legendItem}>
                  <Text style={styles.legendPercentage}>{distribution.excellent ?? 0} %</Text>
                  <View style={styles.legendLabelContainer}>
                    <Text style={styles.legendLabel}>{t('status.excellent')}</Text>
                    <View style={[styles.legendBar, { backgroundColor: '#2ca02c' }]} />
                  </View>
                </View>
                <View style={styles.legendItem}>
                  <Text style={styles.legendPercentage}>{distribution.good ?? 0} %</Text>
                  <View style={styles.legendLabelContainer}>
                    <Text style={styles.legendLabel}>{t('status.good')}</Text>
                    <View style={[styles.legendBar, { backgroundColor: '#ff7f0e' }]} />
                  </View>
                </View>
                <View style={styles.legendItem}>
                  <Text style={styles.legendPercentage}>{distribution.needs_improvement ?? 0} %</Text>
                  <View style={styles.legendLabelContainer}>
                    <Text style={styles.legendLabel}>{t('status.needsImprovement')}</Text>
                    <View style={[styles.legendBar, { backgroundColor: '#d62728' }]} />
                  </View>
                </View>
              </View>
            </View>
          </View>
        </View>

        {/* Details Section (التفاصيل) */}
        {(service.service_fees || service.requirements_conditions || service.steps_to_obtain ||
          service.delivery_channels || service.target_customers || service.service_limitations ||
          service.service_hours || service.additional_info) && (
          <View style={[styles.detailsCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
            <Text style={[styles.detailsTitle, { color: themeColors.textPrimary }]}>{t('services.details')}</Text>

            {service.service_fees ? (
              <View style={styles.detailItem}>
                <View style={styles.detailItemHeader}>
                  <Text style={[styles.detailItemLabel, { color: themeColors.textPrimary }]}>{t('services.serviceFees')}</Text>
                </View>
                <Text style={styles.detailItemValue}>{service.service_fees}</Text>
              </View>
            ) : null}

            {service.requirements_conditions ? (
              <View style={styles.detailItem}>
                <View style={styles.detailItemHeader}>
                  <Text style={[styles.detailItemLabel, { color: themeColors.textPrimary }]}>{t('services.requirementsConditions')}</Text>
                </View>
                <Text style={styles.detailItemValue}>{service.requirements_conditions}</Text>
              </View>
            ) : null}

            {service.steps_to_obtain ? (
              <View style={styles.detailItem}>
                <View style={styles.detailItemHeader}>
                  <Text style={[styles.detailItemLabel, { color: themeColors.textPrimary }]}>{t('services.stepsToObtain')}</Text>
                </View>
                <Text style={styles.detailItemValue}>{service.steps_to_obtain}</Text>
              </View>
            ) : null}

            {service.delivery_channels ? (
              <View style={styles.detailItem}>
                <View style={styles.detailItemHeader}>
                  <Text style={[styles.detailItemLabel, { color: themeColors.textPrimary }]}>{t('services.deliveryChannels')}</Text>
                </View>
                <Text style={styles.detailItemValue}>{service.delivery_channels}</Text>
              </View>
            ) : null}

            {service.target_customers ? (
              <View style={styles.detailItem}>
                <View style={styles.detailItemHeader}>
                  <Text style={[styles.detailItemLabel, { color: themeColors.textPrimary }]}>{t('services.targetCustomers')}</Text>
                </View>
                <Text style={styles.detailItemValue}>{service.target_customers}</Text>
              </View>
            ) : null}

            {service.service_limitations ? (
              <View style={styles.detailItem}>
                <View style={styles.detailItemHeader}>
                  <Text style={[styles.detailItemLabel, { color: themeColors.textPrimary }]}>{t('services.serviceLimitations')}</Text>
                </View>
                <Text style={styles.detailItemValue}>{service.service_limitations}</Text>
              </View>
            ) : null}

            {service.service_hours ? (
              <View style={styles.detailItem}>
                <View style={styles.detailItemHeader}>
                  <Text style={[styles.detailItemLabel, { color: themeColors.textPrimary }]}>{t('services.serviceHours')}</Text>
                </View>
                <Text style={styles.detailItemValue}>{service.service_hours}</Text>
              </View>
            ) : null}

            {service.additional_info ? (
              <View style={styles.detailItem}>
                <View style={styles.detailItemHeader}>
                  <Text style={[styles.detailItemLabel, { color: themeColors.textPrimary }]}>{t('services.additionalInfo')}</Text>
                </View>
                <Text style={styles.detailItemValue}>{service.additional_info}</Text>
              </View>
            ) : null}
          </View>
        )}

        {/* Bottom Section Grid - KPIs (2 cols) + Comments (1 col) */}
        <View style={[styles.bottomGrid, isSmallScreen && styles.bottomGridSmall]}>
          {/* Comments Section (1 column) - LEFT - Unified with GoalDetailScreen */}
          <View style={[styles.commentsColumn, isSmallScreen && styles.commentsColumnSmall]}>
            <CommentSection
              title={t('comments.title')}
              comments={comments}
              showAddComment={showAddComment}
              editingComment={editingComment}
              hideAddButton={!canComment}
              onAddClick={() => setShowAddComment(true)}
              onEditComment={handleEditComment}
              onDeleteComment={handleDeleteComment}
              onSubmitComment={handleCommentSubmit}
              onCancelComment={handleCommentCancel}
            />
          </View>

          {/* KPIs Section (2 columns) - RIGHT */}
          <View style={[styles.kpisSection, isSmallScreen && styles.kpisSectionSmall]}>
            <View style={[styles.kpisSectionCard, { backgroundColor: themeColors.card, borderColor: themeColors.cardBorder }]}>
              {/* Header */}
              <View style={styles.kpisSectionHeader}>
                <Text style={[styles.kpisSectionTitle, { color: themeColors.textPrimary }]}>{t('services.serviceLinks')}</Text>
              </View>

              {/* KPIs Tab Header */}
              <View style={styles.kpisTabHeader}>
                {!isViewOnly && (
                  <TouchableOpacity
                    style={styles.addKpiButton}
                    onPress={() => navigation.navigate('AddService', { serviceId: id, scrollToKpi: true })}
                  >
                    <PlusIcon size={13} color={tokens.colors.semantic.general.foreground} />
                    <Text style={styles.addKpiButtonText}>{t('kpis.addKPI')}</Text>
                  </TouchableOpacity>
                )}
                <View style={styles.kpisTabTitleContainer}>
                  <Text style={styles.kpisTabTitle}>{t('kpis.performanceIndicators')}</Text>
                </View>
              </View>

              {/* KPIs Horizontal Scroll with RTL transform */}
              <View style={styles.rtlWrapper}>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={true}
                  contentContainerStyle={styles.kpisScrollContent}
                  style={styles.horizontalScrollRTL}
                >
                  {kpis.map((kpi) => {
                    const achievement = kpi.achievement_percentage;
                    const achievedColor = achievement >= 80 ? '#2ca02c' : achievement >= 50 ? tokens.colors.raw.yellow["500"] : tokens.colors.raw.red["500"];
                    return (
                      <View key={kpi.id} style={styles.kpiCardWrapper}>
                        <TargetAchievementCard
                          title={translate(kpi.name)}
                          achieved={kpi.latest_value || '0'}
                          target={kpi.target_value}
                          unit={kpi.unit === 'percentage' ? '%' : translate(kpi.unit === 'count' ? 'عدد' : kpi.unit)}
                          achievedColor={achievedColor}
                          percentage={achievement}
                          onViewDetails={() => navigation.navigate('KPIDetail', { id: kpi.id })}
                        />
                      </View>
                    );
                  })}
                  {kpis.length === 0 && (
                    <View style={styles.kpiCardWrapper}>
                      <Text style={styles.emptyStateText}>{t('kpis.noKPIs')}</Text>
                    </View>
                  )}
                </ScrollView>
              </View>
            </View>
          </View>
        </View>
        <EvidenceTable
          relatedType="service"
          filterRelatedId={id}
          title={t('evidence.sectionTitle')}
          canManage={canManageEvidence}
          showOrgColumn={canViewAllEvidence}
          onAddPress={() => navigation.navigate('AddEvidence', {
            relatedType: 'service', relatedId: id, contextLabel: t('services.title'),
          })}
          onEditPress={(ev) => navigation.navigate('AddEvidence', {
            evidenceId: ev.id, relatedType: ev.related_type, relatedId: ev.related_id, contextLabel: t('services.title'),
          })}
        />
      </ScrollView>

      {/* Deletion Impact Modal */}
      <DeletionImpactModal
        visible={showDeletionImpact}
        onClose={() => setShowDeletionImpact(false)}
        onConfirm={confirmDelete}
        entityName={service?.name || ''}
        fetchImpact={async () => {
          const response = await servicesApi.deletionImpact(id);
          return response.data;
        }}
        isOrgEmployee={isOrgEmployee}
        isSubmitting={isDeleting}
      />

    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  content: {
    padding: tokens.spacing.absolute["6"],
  },
  contentSmall: {
    padding: tokens.spacing.absolute["3"],
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.red["500"],
  },
  // Breadcrumb
  breadcrumb: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  breadcrumbSmall: {
    flexWrap: 'wrap',
    gap: tokens.spacing.absolute["1"],
    marginBottom: tokens.spacing.absolute["4"],
  },
  breadcrumbLink: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.gray["500"],
  },
  breadcrumbActive: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general["Main Color"],
  },
  headerActions: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute["5"],
  },
  // Stats Row
  statsRow: {
    flexDirection: 'row-reverse',
    gap: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["6"],
  },
  statsRowSmall: {
    flexDirection: 'column',
    gap: tokens.spacing.absolute["4"],
  },
  statsCard: {
    flex: 1,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["5"],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    overflow: 'hidden',
  },
  statsCardFixed: {
    width: 280,
    minWidth: 280,
    flexGrow: 0,
    flexShrink: 0,
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["5"],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  statsCardTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 24,
    marginBottom: tokens.spacing.absolute["5"],
  },
  // Basic Info Card
  infoItemsContainer: {
    gap: tokens.spacing.absolute["6"],
  },
  infoItem: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
    gap: tokens.spacing.absolute["2"],
  },
  infoLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: '#44546A',
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  infoValue: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 24,
  },
  // Chart Container
  chartContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.absolute["4"],
  },
  // Distribution Content
  distributionContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  legendContainer: {
    gap: tokens.spacing.absolute["6"],
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["2"],
  },
  legendLabelContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["1"],
    width: 80,
  },
  legendBar: {
    width: 8,
    height: 32,
    borderRadius: tokens.radii.absolute["radius-2"],
  },
  legendLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    lineHeight: 14,
  },
  legendPercentage: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.medium,
    color: '#303030',
  },
  // Details Section
  detailsCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["5"],
    marginBottom: tokens.spacing.absolute["6"],
    gap: tokens.spacing.absolute["5"],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  detailsTitle: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 24,
  },
  detailItem: {
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-4"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingVertical: tokens.spacing.absolute["1"],
    gap: tokens.spacing.absolute["1"],
  },
  detailItemHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["2_5"],
  },
  detailItemLabel: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    lineHeight: 20,
  },
  detailItemValue: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    fontFamily: FONTS.regular,
    color: '#44546A',
    textAlign: getTextAlign(isRTL),
    lineHeight: 14,
  },
  // Bottom Grid (2-column layout)
  bottomGrid: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["5"],
  },
  bottomGridSmall: {
    flexDirection: 'column-reverse',
    gap: tokens.spacing.absolute["4"],
  },
  // Comments Column - Unified with GoalDetailScreen
  commentsColumn: {
    flex: 1,
  },
  commentsColumnSmall: {
    flex: 0,
  },
  // KPIs Section (Right column - 2x width)
  kpisSection: {
    flex: 2,
  },
  kpisSectionSmall: {
    flex: 0,
  },
  kpisSectionCard: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-8"],
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    padding: tokens.spacing.absolute["3"],
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    overflow: 'hidden',
  },
  kpisSectionHeader: {
    marginBottom: tokens.spacing.absolute["6"],
  },
  kpisSectionTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    width: '100%',
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
  },
  kpisTabHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: tokens.spacing.absolute["5"],
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    marginBottom: tokens.spacing.absolute["2_5"],
  },
  addKpiButton: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: tokens.spacing.absolute["1_5"],
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["300"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingHorizontal: tokens.spacing.absolute["3"],
    paddingVertical: 5.5,
    minHeight: 32,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  addKpiButtonText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
  },
  kpisTabTitleContainer: {
    flex: 1,
    alignItems: 'flex-end',
    width: '100%',
  },
  kpisTabTitle: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    writingDirection: 'rtl',
  },
  // KPIs Horizontal Scroll - RTL wrapper
  rtlWrapper: {
    transform: [{ scaleX: -1 }],
  },
  horizontalScrollRTL: {
    flexGrow: 0,
  },
  kpisScrollContent: {
    flexDirection: 'row',
    gap: tokens.spacing.absolute["6"],
    paddingHorizontal: tokens.spacing.absolute["2"],
    paddingBottom: tokens.spacing.absolute["3"],
  },
  kpiCardWrapper: {
    width: 280,
    transform: [{ scaleX: -1 }],
  },
  // Empty State
  emptyState: {
    flex: 1,
    padding: tokens.spacing.absolute["6"],
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    textAlign: 'center',
    padding: tokens.spacing.absolute["5"],
  },
  // Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: tokens.colors.raw.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    padding: tokens.spacing.absolute["6"],
    width: '90%',
    maxWidth: 500,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  modalTitle: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["3"],
    lineHeight: 26,
  },
  modalMessage: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.raw.slate["500"],
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["4"],
    lineHeight: 24,
  },
  modalInput: {
    borderWidth: tokens.spacing.absolute["px"],
    borderColor: tokens.colors.raw.slate["200"],
    borderRadius: tokens.radii.absolute["radius-8"],
    padding: tokens.spacing.absolute["3"],
    fontSize: tokens.typography.paragraph["paragraph small font size"],
    fontFamily: FONTS.regular,
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
    minHeight: 100,
    marginBottom: tokens.spacing.absolute["5"],
  },
  modalButtons: {
    flexDirection: 'row-reverse',
    gap: tokens.spacing.absolute["3"],
  },
  modalCancelButton: {
    flex: 1,
    backgroundColor: tokens.colors.raw.slate["100"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["3"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalCancelButtonText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.slate["500"],
    lineHeight: 20,
  },
  modalRejectButton: {
    flex: 1,
    backgroundColor: tokens.colors.raw.red["500"],
    borderRadius: tokens.radii.absolute["radius-8"],
    paddingVertical: tokens.spacing.absolute["3"],
    alignItems: 'center',
    justifyContent: 'center',
  },
  modalRejectButtonDisabled: {
    opacity: 0.5,
  },
  modalRejectButtonText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontFamily: FONTS.medium,
    color: tokens.colors.raw.white,
    lineHeight: 20,
  },
});

const styles = getStyles(true);
